import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { TranslocoService } from '@ngneat/transloco';
import * as moment from 'moment';
import {
  AddContractInterface,
  ContractInterface,
} from '../interfaces/contract.interface';
import { PointInterface } from '../interfaces/point.interface';
import { AppService } from './app.service';
import { ContractsApiService } from './contracts-api.service';
import { PermissionsService } from './permissions.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class ContractsService {
  public contracts: ContractInterface[] = [];
  public email: string = '';
  public location: string = '';
  public points: PointInterface[] = [];
  public nextPickupPointIndex: number | null = null;
  public nextPickupDate: Date;
  public bdoUUID: string = '';
  public avatarTextSizeRatio: number = 2;
  public selectedContractIndex: number | null = null;
  public showMobileDocumentFilesComponent: boolean = false;
  public hasAdrContract: boolean = false;
  public adrContractUUID: string = '';
  private _daysToNextPickup: number;
  private _name: string = '';

  get mainContract() {
    if (this.contracts.length > 0) {
      return this.contracts[0];
    } else {
      return null;
    }
  }

  get selectedContract() {
    if (this.selectedContractIndex == null) {
      return null;
    } else {
      return this.contracts[this.selectedContractIndex];
    }
  }

  get name() {
    if (this._name?.length > 0) {
      return this._name;
    } else {
      return this.translationService.translate('profile.unknownName');
    }
  }

  get daysToNextPickup() {
    if (this._daysToNextPickup != null) {
      return (
        this._daysToNextPickup.toString() +
        ' ' +
        this.translationService.translate('profile.days')
      );
    } else {
      return null;
    }
  }

  get nextPickupPoint() {
    if (this.nextPickupPointIndex == null) {
      return null;
    } else {
      return this.points[this.nextPickupPointIndex];
    }
  }

  get nextPickupLocation() {
    if (this.nextPickupPoint?.adresFull?.length > 0) {
      return this.nextPickupPoint?.adresFull;
    } else {
      return null;
    }
  }

  get isInDebt() {
    //TODO - waiting on backend
    return false;
    // return true;
  }

  constructor(
    private appService: AppService,
    private storageService: StorageService,
    private translationService: TranslocoService,
    private permissionsService: PermissionsService,
    private contractsApiService: ContractsApiService
  ) {}

  public async loadProfileData(): Promise<void> {
    if (this.permissionsService.hasRestrictedAccess) {
      await this.loadRestrictedUserData();
      await this.loadRestrictedAccessWasteCodes();
    } else {
      await this.loadContracts();
      await this.loadDaysToNextPickup();
    }
  }

  public checkIfContractExists(addContractDto: AddContractInterface): boolean {
    const addedContractsDtos: AddContractInterface[] =
      this.storageService.get('contracts');

    if (addedContractsDtos) {
      for (const addedContractDto of addedContractsDtos) {
        if (
          addedContractDto.kodAOK === addContractDto.kodAOK &&
          addedContractDto.numerUmowy === addContractDto.numerUmowy
        ) {
          return true;
        }
      }
    }

    return false;
  }

  public async addContract(
    addContractDto: AddContractInterface
  ): Promise<void> {
    const addContractResponse = await this.contractsApiService.addContract(
      addContractDto
    );
    addContractDto.token = addContractResponse.token;

    let contracts: AddContractInterface[] =
      this.storageService.get('contracts');

    if (contracts) {
      contracts.push(addContractDto);
    } else {
      contracts = [addContractDto];
    }

    this.storageService.set('contracts', contracts);
    this.loadProfileData();
  }

  public selectContract(contractIndex: number): void {
    this.selectedContractIndex = contractIndex;
    this.showMobileDocumentFilesComponent = false;
  }

  public clearSelectedContract(): void {
    this.selectedContractIndex = null;
  }

  private async loadRestrictedUserData(): Promise<void> {
    const restrictedUserData =
      await this.contractsApiService.getRestrictedUserData();

    if (restrictedUserData) {
      this._name = restrictedUserData.podmiot.nazwa;
      this.updateAvatarTextSize();
      this.email = this.storageService.get('email');
      this.location = restrictedUserData.podmiot.address;
    }
  }

  private async loadRestrictedAccessWasteCodes(): Promise<void> {
    const wasteCodesData =
      await this.contractsApiService.getRestrictedWasteCodes();
    this.storageService.set('wasteCodes', wasteCodesData.kody);
  }

  private async loadContracts(): Promise<void> {
    this.contracts = [];
    if (Capacitor.isNativePlatform()) {
      await this.loadContractsNative();
    } else {
      await this.loadContractsWeb();
    }
  }

  private async loadContractsNative(): Promise<void> {
    const contracts = [];
    const mainContract = await this.contractsApiService.getContract();
    if (mainContract?.email) {
      this.email = mainContract.email;
    }
    mainContract.token = this.storageService.get('token');
    contracts.push(mainContract);

    const addedContractsDtos: AddContractInterface[] =
      this.storageService.get('contracts');
    if (addedContractsDtos) {
      for (const dto of addedContractsDtos) {
        let contract = await this.contractsApiService.getContract(dto);
        contract.token = dto.token;
        contracts.push(contract);
      }
    }

    if (mainContract != null) {
      this.storageService.set('wasteCodes', mainContract.kodyOdpadu);
    }

    let points = [];
    for (const [index, contract] of contracts.entries()) {
      const pointsJson = await this.contractsApiService.getContractPoints(
        contract.token
      );

      if (pointsJson?.elementy != null) {
        const contractWithExtras = {
          ...contract,
          points: pointsJson.elementy,
        };

        this.parseContractLocationData(contractWithExtras);
        if (index === 0) {
          this.location = contractWithExtras.points[0].adresFull;
          this.parseLocationProfileData(contractWithExtras.points[0]);
        }
        points = points.concat(pointsJson.elementy);
        this.contracts.push(contractWithExtras);
      }
    }

    this.points = points;

    if (mainContract?.dodatkoweUmowy?.length > 0) {
      this.hasAdrContract = true;
      this.adrContractUUID = mainContract?.dodatkoweUmowy[0]?.uuid;

      for (const additionalContract of mainContract?.dodatkoweUmowy) {
        const additionalContractDto: AddContractInterface = {
          numerUmowy: additionalContract.numer,
          kodAOK: additionalContract.aok,
          token: additionalContract.uuid,
        };

        let contract = await this.contractsApiService.addContract(
          additionalContractDto
        );
        contract.token = additionalContractDto.token;

        const contractWithExtras = {
          ...contract,
          points: [],
          iloscPunktow: 0,
        };
        this.contracts.push(contractWithExtras);
      }
    } else {
      this.hasAdrContract = false;
    }
  }

  private async loadContractsWeb(): Promise<void> {
    const mainContractToken = this.storageService.get('token');
    const contracts: any[] = await this.contractsApiService.getContract();

    if (contracts?.length > 0) {
      this.storageService.set('wasteCodes', contracts[0].kodyOdpadu);
    }

    const pointsJson = await this.contractsApiService.getContractPoints(
      mainContractToken
    );
    this.points = pointsJson.elementy;

    const groupedPoints = {};
    for (const point of this.points) {
      if (!(point.umowa_UUID in groupedPoints)) {
        groupedPoints[point.umowa_UUID] = [point];
      } else {
        groupedPoints[point.umowa_UUID].push(point);
      }
    }

    const documentFiles = await this.contractsApiService.getContractFileList(
      mainContractToken
    );

    const groupedDocumentFiles = {};
    for (const documentFile of documentFiles) {
      if (!(documentFile.umowa_UUID in groupedDocumentFiles)) {
        groupedDocumentFiles[documentFile.umowa_UUID] = [documentFile];
      } else {
        groupedDocumentFiles[documentFile.umowa_UUID].push(documentFile);
      }
    }

    let profileSaved = false;

    for (const contract of contracts) {
      if (!contract.token) {
        contract.token = mainContractToken;
      }
      const contractPoints = groupedPoints[contract.umowaUUID];
      const contractDocumentFiles = groupedDocumentFiles[contract.umowaUUID];

      let contractWithExtras = {
        ...contract,
      };

      if (contractPoints?.length > 0) {
        contractWithExtras = {
          ...contract,
          points: contractPoints,
        };

        if (!profileSaved) {
          const pointFixed = this.parsePointLocationData(
            contractWithExtras.points[0]
          );
          this.email = contractWithExtras?.email;
          this.location = pointFixed.adresFull;
          this.parseLocationProfileData(pointFixed);
          profileSaved = true;
        }
      }

      let isDocusigned = false;
      if (contractDocumentFiles?.length > 0) {
        contractWithExtras = {
          ...contractWithExtras,
          documentFiles: contractDocumentFiles,
        };

        for (const documentFile of contractDocumentFiles) {
          if (
            documentFile?.nazwaPliku?.toUpperCase().includes('DOCUSIGN') !==
              -1 ||
            documentFile?.opis?.toUpperCase().includes('DOCUSIGN')
          ) {
            isDocusigned = true;
            break;
          }
        }
      } else {
        contractWithExtras = {
          ...contractWithExtras,
          documentFiles: [],
        };
      }

      if (!isDocusigned) {
        let docusignWarningDescription =
          'Skontaktuj się z BOK, jeśli chcesz otrzymać skan umowy';
        if (this.appService.isPartnerVersion) {
          docusignWarningDescription =
            'Skontaktuj się z nami, jeśli chcesz otrzymać skan umowy';
        }

        contractWithExtras.documentFiles.unshift({
          isDocusignWarning: true,
          opis: docusignWarningDescription,
        });
      }

      this.contracts.push(contractWithExtras);
    }
  }

  private parseContractLocationData(contract: ContractInterface): void {
    const newPoints = [];

    contract.points.forEach((locationAddresses) => {
      let location = '';

      if (locationAddresses.adres_Miasto?.length > 0) {
        location += locationAddresses.adres_Miasto;

        if (locationAddresses.adres_Ulica?.length > 0) {
          location += ', ' + locationAddresses.adres_Ulica;

          if (locationAddresses.adres_NrDomu?.length > 0) {
            location += ' ' + locationAddresses.adres_NrDomu;

            if (locationAddresses.adres_NrLokalu?.length > 0) {
              location += '/' + locationAddresses.adres_NrLokalu;

              if (locationAddresses.adres_KodPocztowy?.length > 0) {
                location += ', ' + locationAddresses.adres_KodPocztowy;
              }
            }
          }
        }
      }

      locationAddresses = {
        ...locationAddresses,
        adresFull: location,
      };
      newPoints.push(locationAddresses);
    });

    contract.points = newPoints;
  }

  private parsePointLocationData(point: PointInterface): PointInterface {
    let location = '';

    if (point.adres_Miasto?.length > 0) {
      location += point.adres_Miasto;

      if (point.adres_Ulica?.length > 0) {
        location += ', ' + point.adres_Ulica;

        if (point.adres_NrDomu?.length > 0) {
          location += ' ' + point.adres_NrDomu;

          if (point.adres_NrLokalu?.length > 0) {
            location += '/' + point.adres_NrLokalu;

            if (point.adres_KodPocztowy?.length > 0) {
              location += ', ' + point.adres_KodPocztowy;
            }
          }
        }
      }
    }

    const pointFixed = {
      ...point,
      adresFull: location,
    };
    return pointFixed;
  }

  private parseLocationProfileData(point: PointInterface): void {
    if (point.bdO_UUID?.length > 0) {
      this.bdoUUID = point.bdO_UUID;
    }

    if (point.nazwa?.length > 0) {
      this._name = point.nazwa;
      this.updateAvatarTextSize();
    }

    // The email is now taken from the "email" field of the contract data.
    // This code snippet is kept here for a month, in case the field gets changed back
    //
    // if (point.adres_Poczta?.length > 0) {
    //   this.email = point.adres_Poczta;
    // }
  }

  private async loadDaysToNextPickup(): Promise<void> {
    // Server sends error 404 and trips the try-catch block if there's no pickups planned.
    // The next pickup data is cleared to make sure that the dashboard won't show previous pickup value even
    // if such situation occurs.
    //
    // If the user cancels the last planned pickup, the dashboard should refresh and show all
    // the next pickup values as unavailable.
    this.clearNextPickupData();

    let contractUUIDs = [];
    this.contracts.forEach((contract) => {
      contractUUIDs.push(contract.umowaUUID);
    });

    let nextPickupData;
    try {
      nextPickupData = await this.contractsApiService.getNextPickupData(
        contractUUIDs
      );
    } catch (error) {
      return;
    }

    let newDaysToNextPickup = moment(nextPickupData?.data).diff(
      moment().subtract(1, 'days'),
      'days'
    );

    if (newDaysToNextPickup < 0) {
      newDaysToNextPickup = 0;
    }

    let newNextPickupPointIndex = null;
    for (let pointsIndex = 0; pointsIndex < this.points.length; ++pointsIndex) {
      const point = this.points[pointsIndex];
      if (point.uuid == nextPickupData?.punktUUID) {
        newNextPickupPointIndex = pointsIndex;
        this.points[pointsIndex] = this.parsePointLocationData(point);
        break;
      }
    }

    this._daysToNextPickup = newDaysToNextPickup;
    this.nextPickupDate = new Date(nextPickupData?.data);
    this.nextPickupPointIndex = newNextPickupPointIndex;
  }

  private clearNextPickupData(): void {
    this._daysToNextPickup = undefined;
    this.nextPickupDate = undefined;
    this.nextPickupPointIndex = null;
  }

  private updateAvatarTextSize(): void {
    this.avatarTextSizeRatio = 2 + this._name.split(' ').length / 2;
  }
}
